import * as React from "react";
import ScheduleTeamPickerUtils from "./ScheduleTeamPickerUtils";
import Spinner, { SpinnerSize } from "sh-application/components/common/Spinner";
import StringsStore from "sh-strings/store";
import { AriaProperties, AriaRoles, generateDomPropertiesForAria } from "owa-accessibility";
import { computed } from "mobx";
import { FocusZone, MessageBarType } from "@fluentui/react";
import { Icon } from "@fluentui/react";
import { ITeamInfoEntity, TeamCategoryTypes } from "sh-models";
import { Menu, MenuTrigger, MenuPopover, MenuList, MenuItem } from "@fluentui/react-menu";
import { Button, Checkbox, FluentProvider, useRestoreFocusTarget } from "@fluentui/react-components";
import { MoreHorizontal20Regular, Delete20Regular } from "@fluentui/react-icons";
import { TreeItem, TreeItemLayout } from "@fluentui/react-components";
import { MobxUtils, restartApp } from "sh-application";
import { observer } from "mobx-react";
import { ScheduleTeamPickerProps } from "./ScheduleTeamPickerUtils";
import { SET_INITIAL_FOCUS_DELAY_MS } from "sh-application/utility/UiUtils";
import { TeamInfoStore } from "sh-stores";
import { TeamStore } from "sh-team-store";
import { ECSConfigKey, ECSConfigService, InstrumentationService } from "sh-services";
import teamSetupPickerStore from "../store/store";
import { Archive16Regular } from '@fluentui/react-icons';
import MemberUtils from "sh-application/utility/MemberUtils";
import ThemeUtils from "sh-application/utility/ThemeUtils";
import { NewShiftsDialog } from "../../common/NewShiftsDialog/NewShiftsDialog";
import { deleteSchedule } from "../../schedules/lib";
import { getGenericEventPropertiesObject } from "sh-instrumentation";
import RestClient from "sh-rest-client";
import setGlobalMessage from "sh-application/actions/setGlobalMessage";
import { appViewState } from "sh-application/store";
import { MESSAGE_BAR_AUTO_DISMISS_ANIMATION_TIME_MS, MESSAGE_BAR_AUTO_DISMISS_DELAY_MS } from "sh-application/../StaffHubConstants";
import { fireAccessibilityAlert } from "../../accessibilityAlert";

let classNames = require("classnames/bind");
const styles = require("./ScheduleTeamPickerPane.scss");
const EmptyListSVG = require("-!svg-react-loader?name=SVGImage!sh-assets/images/optimized-svg/no-results-image.svg");
const showIcons = false;

/**
 * React component that renders the schedule team picker UX
 */
@observer
export default class ScheduleTeamPickerPanel extends React.Component<ScheduleTeamPickerProps, any> {
    private _strings: Map<string, string>;
    private _commonStrings: Map<string, string>;
    private _firstItemRef = React.createRef<HTMLDivElement>();
    private _isSeperatedArchiveTeamsViewEnabled: boolean = ECSConfigService.isECSFeatureEnabled(ECSConfigKey.SeparateArchiveTeamsViewEnabled);

    constructor(props: ScheduleTeamPickerProps) {
        super(props);
        this._strings = StringsStore().registeredStringModules.get("scheduleTeamPicker").strings;
        this._commonStrings = StringsStore().registeredStringModules.get("common").strings;
        this.state = {isDeleteScheduleDailogOpen: false, teamScheduleName: "", isDeleteScheduleConfirmed: false, toDeleteScheduleTeamId: "", userAdminInfoMap: {}};
    }

    async componentDidMount() {
        await ScheduleTeamPickerUtils.loadInitialData(this.props.instrumentationEventName);

        await this.loadTeamAdminInformation();

        if (this.props.autofocus) {
            // Set initial focus on the first team item
            // setTimeout is needed for setting focus to succeed. One alternative might be to get the child item
            // to set focus.
            setTimeout(() => {
                if (this._firstItemRef && this._firstItemRef.current) {
                    this._firstItemRef.current.focus();
                }
            }, SET_INITIAL_FOCUS_DELAY_MS);
        }
    }

    private async loadTeamAdminInformation(): Promise<void> {
        try {
            const teamsWithAdminInfo = await RestClient.getTeamsWithAdminInfo();
            this.setState({
                userAdminInfoMap: teamsWithAdminInfo.userAdminInfoMap || {}
            });
        } catch (error) {
            console.error("Failed to load user isAdmin information for non current teams:", error);
            InstrumentationService.logPerfEvent(InstrumentationService.perfScenarios.DeleteSchedule,
                [getGenericEventPropertiesObject(InstrumentationService.properties.LoadAdminInfoFailure, true)]
            );
        }
    }

    /**
     * Calculate the schedule teams list data which will be used for rendering
     */
    @computed
    private get calculateScheduleTeamsList(): ITeamInfoEntity[] {
        let teamInfoArray: ITeamInfoEntity[] = [];
        const teamInfos = TeamInfoStore().teams;
        if (teamInfos) {
            teamInfoArray = MobxUtils.MapToArray(teamInfos);
            teamInfoArray = teamInfoArray.filter((teamInfo) => !teamInfo.isSoftDeleted).sort(ScheduleTeamPickerUtils.scheduleTeamInfoComparator);
        }
        return teamInfoArray;
    }

    /**
     *
     * @param selectedTeamCategoryType The selected team category type.
     * @returns The list of teams based on the selected team category type.
     * Gets the list of teams based on the selected team category type.
     */
    private getSelectedTeamCategoryScheduleTeamsList(): ITeamInfoEntity[] {
        const teamInfos = this.calculateScheduleTeamsList;
        switch (teamSetupPickerStore.selectedTeamCategoryType) {
            case TeamCategoryTypes.Active:
                return teamInfos.filter((teamInfo: ITeamInfoEntity) => !teamInfo.isArchived);
            case TeamCategoryTypes.Archived:
                return teamInfos.filter((teamInfo: ITeamInfoEntity) => teamInfo.isArchived);
            case TeamCategoryTypes.All:
                return teamInfos;
            default:
                return [];
        }
    }

    /**
     * Renders loading progress state
     */
    private renderLoadingProgress(): JSX.Element {
        return (
            <div className={ styles.progressIndicatorContainer }>
                <Spinner className={ styles.spinner } size={ SpinnerSize.small } label={ this._strings.get("loadingTeamsMessage") } />
            </div>
        );
    }

    /**
     * Render empty state when no teams are available to be listed
     */
    private renderEmptyTeamListState(): JSX.Element {
        const description = teamSetupPickerStore.selectedTeamCategoryType === TeamCategoryTypes.Archived ? this._strings.get("emptyArchiveTeamListDescription") : teamSetupPickerStore.selectedTeamCategoryType === TeamCategoryTypes.Active ? this._strings.get("emptyActiveTeamListDescription") : null;
        return (
            !this._isSeperatedArchiveTeamsViewEnabled ?
            // Display nothing. Entrypoints for the schedule team picker should have been disabled in the first place. this only applies when this._isSeperatedArchiveTeamsViewEnabled is false
            <></>
            :
            (
                <div className={ styles.emptyStateContainer } data-is-focusable={true} role={AriaRoles.region} aria-live="assertive">
                    <EmptyListSVG aria-hidden="true" focusable="false" className={ styles.emptyStateImage } />
                    <div className={ styles.emptyStateContent }>
                        <h2 className={ styles.emptyStateTitle } aria-label={ this._strings.get("emptyListTitle") }>
                            { this._strings.get("emptyListTitle") }
                        </h2>
                        <p className={ styles.emptyStateDescription } aria-label={ description }>
                            { description }
                        </p>
                    </div>
                </div>
            )
        );
    }

    /**
     * Handler for when a View team button is clicked
     */
    private handleViewTeamButtonClicked(teamInfo: ITeamInfoEntity) {
        if (this.props.onTeamSelected) {
            this.props.onTeamSelected(teamInfo);
            const teamName = teamInfo.name;
            if (teamName) {
                fireAccessibilityAlert(this._strings.get("selectTeamPanelAlert") + " " + teamName);
            }
            if (this._isSeperatedArchiveTeamsViewEnabled) {
                ScheduleTeamPickerUtils.logScheduleSelectionEvent(this.props.instrumentationEventName, teamInfo.isArchived);
            }
        }
    }

    /**
     * Render the schedule teams picker list contents
    */
    private renderScheduleTeamsPickerListContents(): JSX.Element {
        const teamInfoList: ITeamInfoEntity[] = this._isSeperatedArchiveTeamsViewEnabled ? this.getSelectedTeamCategoryScheduleTeamsList() : this.calculateScheduleTeamsList;
        return (
            ScheduleTeamPickerUtils.isScheduleTeamsDataLoading()
            ?
            this.renderLoadingProgress()
            :
            (
                (teamInfoList && teamInfoList.length > 0)
                ?
                this.renderScheduleTeamsPickerList(teamInfoList)
                :
                this.renderEmptyTeamListState()
            )
        );
    }

    /**
     * Render the schedule teams picker list UX
     */
    private renderScheduleTeamsPickerList(teamInfoList: ITeamInfoEntity[]): JSX.Element {
        return (
            <div className={ styles.list }>
                { this.renderScheduleTeamsListItems(teamInfoList) }
            </div>
        );
    }

    /**
     * Render the list items for the schedule teams list
     */
    private renderScheduleTeamsListItems(teamInfoList: ITeamInfoEntity[]): JSX.Element {
        interface TeamListItemProps {
            item: ITeamInfoEntity;
            index: number;
        }

        const TeamListItem = (props: TeamListItemProps) => {
            // We prevent StaffHub teams from being opened from within the MS Teams application.
            // Users need to visit the old StaffHub Web site in order to view those teams.
            const isFirstItem: boolean = props.index === 0;

            return (
                <>
                    {
                        this.renderScheduleTeamListItemLeft(props.item, isFirstItem)
                    }
                </>);
        };

        const ariaProps: AriaProperties = {
            role: AriaRoles.tablist
        };

        return (
            <FluentProvider theme={ThemeUtils.getFluentV2ShiftTheme()}>
                <div tabIndex={0} className={ styles.tabList } { ...generateDomPropertiesForAria(ariaProps) }>
                    {
                        teamInfoList.map((item, index) => (
                            <TeamListItem key={ item.id } item={ item } index={ index } />
                        ))
                    }
                </div>
            </FluentProvider>
        );
    }

    /**
     * Render the schedule team list item by using Fluent UI TreeItem
     * @param teamInfo The team info
     * @param isFirstItem The first item in the list
     * @returns The schedule team list item element
     */
    private renderScheduleTeamListTreeItem(teamInfo: ITeamInfoEntity, isFirstItem: boolean): JSX.Element {
        const focusTargetAttribute = useRestoreFocusTarget();
        const isCurrentTeam = TeamStore().teamId === teamInfo.id;
        const itemClasses = classNames(styles.listItem, { [`${styles.selected}`]: isCurrentTeam });
        const handleViewButtonClicked = () => { this.handleViewTeamButtonClicked(teamInfo); };
        const ariaProps: AriaProperties = {
            role: AriaRoles.tab,
            selected: isCurrentTeam
        };
        const currentUser = TeamStore() && TeamStore().me;
        const isCurrentTeamAdmin = MemberUtils.isAdmin(currentUser);

        // Determine if action menu should be displayed based on user permissions
        // Logic explanation:
        // 1. For current team: Show if user is admin of current team
        // 2. For other teams: Show if user is admin in those teams (from API response)
        // 3. The userAdminInfoMap contains admin status for each team from the getTeamsWithAdminInfo API
        const showActionMenu = (isCurrentTeam && isCurrentTeamAdmin) ||
            (this.state.userAdminInfoMap && this.state.userAdminInfoMap[teamInfo.id]);

        return (
                    <Menu openOnContext positioning="below-end">
                        <MenuTrigger disableButtonEnhancement>
                                <TreeItem
                                    {...generateDomPropertiesForAria(ariaProps)}
                                    itemType="leaf"
                                    {...focusTargetAttribute}
                                    ref={isFirstItem ? this._firstItemRef : null}
                                    className={itemClasses}
                                    key={teamInfo.id}
                                    aria-hidden="false"
                                    tabIndex={0}
                                    data-is-focusable={true}
                                    onClick={handleViewButtonClicked}>
                                    <TreeItemLayout
                                        actions={
                                            <>
                                                {showActionMenu && (
                                                    <Menu positioning="below-end">
                                                        <MenuTrigger disableButtonEnhancement>
                                                            <Button
                                                                aria-label={this._commonStrings.get("openContextMenuButtonTooltip")}
                                                                appearance="subtle"
                                                                icon={<MoreHorizontal20Regular />}
                                                            />
                                                        </MenuTrigger>
                                                        <MenuPopover>
                                                            <MenuList>
                                                                <MenuItem
                                                                    onClick={() => this.handleDeleteScheduleButtonClick(teamInfo.id)}
                                                                    icon={<Delete20Regular />}>
                                                                    {this._commonStrings.get("delete")}
                                                                </MenuItem>
                                                            </MenuList>
                                                        </MenuPopover>
                                                    </Menu>
                                                )}
                                            </>
                                        }>
                                        <div className={styles.name}>{teamInfo.name}</div>
                                    </TreeItemLayout>
                                </TreeItem>
                        </MenuTrigger>
                        {showActionMenu ? (
                            <MenuPopover>
                                <MenuList>
                                    <MenuItem icon={<Delete20Regular />}>{this._commonStrings.get("delete")}</MenuItem>
                                </MenuList>
                            </MenuPopover>
                        ) : (
                            <div></div>
                        )}
                    </Menu>
        );
    }

    /**
     * Reset the delete schedule state
     * This is used to reset the state of the delete schedule dialog
     * when the dialog is closed or the delete schedule action is completed
     */
    private resetDeleteScheduleState = () => {
        this.setState({ isDeleteScheduleDailogOpen: false, isDeleteScheduleConfirmed: false, teamScheduleName: "", toDeleteScheduleTeamId: "" });
    }

    /**
     * Handle delete schedule button click
     */
    private handleDeleteScheduleButtonClick = (deleteTeamScheduleId: string) => {
        InstrumentationService.logPerfEvent(InstrumentationService.perfScenarios.DeleteSchedule,
            [getGenericEventPropertiesObject(InstrumentationService.properties.DeleteScheduleButtonClicked, true)]
         );
        const teamName = this.calculateScheduleTeamsList.find(teamInfo => teamInfo.id === deleteTeamScheduleId).name;
        this.setState({isDeleteScheduleDailogOpen: true, teamScheduleName: teamName, toDeleteScheduleTeamId: deleteTeamScheduleId});
    }

    /**
     * Handle delete schedule dialog dismiss
     * reset state
     */
    private handleDeleteScheduleDismiss = () => {
         InstrumentationService.logPerfEvent(InstrumentationService.perfScenarios.DeleteSchedule,
            [getGenericEventPropertiesObject(InstrumentationService.properties.IsChangeCanceled, true)]
         );
        this.resetDeleteScheduleState();
    }

    /**
     * Handle delete schedule
     * delete the schedule for the selected team and reset the state
     * dispatch the deleteSchedule action with the team info and teams list
     */
    private handleDeleteSchedule = () => {
        InstrumentationService.logPerfEvent(InstrumentationService.perfScenarios.DeleteSchedule,
            [getGenericEventPropertiesObject(InstrumentationService.properties.Confirmed, true)]
         );
        const teams = this.calculateScheduleTeamsList;
        const teamInfo = teams.find(teamInfo => teamInfo.id === this.state.toDeleteScheduleTeamId);
        const isCurrentTeam = TeamStore().teamId === this.state.toDeleteScheduleTeamId;

        deleteSchedule(teamInfo, teams, (deletedTeam) => this.onDeleteScheduleSuccess(isCurrentTeam, deletedTeam), (error) => this.onDeleteScheduleFailure(isCurrentTeam, error));
        this.resetDeleteScheduleState();
    }

    private onDeleteScheduleSuccess = (isCurrentTeam: boolean, deletedTeam: ITeamInfoEntity) => {
        InstrumentationService.logPerfEvent(InstrumentationService.perfScenarios.DeleteSchedule, [
            getGenericEventPropertiesObject(InstrumentationService.properties.CompletedSuccessfully, true)
        ]);
        setGlobalMessage(
            appViewState().globalMessageViewState,
            this._commonStrings.get("scheduleDeleteSuccess"),
            MessageBarType.success,
            null,
            null,
            true,
            true,
            MESSAGE_BAR_AUTO_DISMISS_DELAY_MS,
            MESSAGE_BAR_AUTO_DISMISS_ANIMATION_TIME_MS
        );
        if (isCurrentTeam) {
            setTimeout(() => {
                restartApp();
            }, 2000);
        }
    }

    private onDeleteScheduleFailure = (isCurrentTeam: boolean, error: any) => {
        // Log performance event for failure
        InstrumentationService.logPerfEvent(InstrumentationService.perfScenarios.DeleteSchedule, [
            getGenericEventPropertiesObject(InstrumentationService.properties.CompletedSuccessfully, false),
            getGenericEventPropertiesObject(InstrumentationService.properties.ErrorMessage, error && error.message || "Unknown error")
        ]);
        // Log error event for detailed analysis
        InstrumentationService.trackException(
            error instanceof Error ? error : new Error(String(error)),
            "delete_schedule_failure",
            "server_response_failure"
        );
        setGlobalMessage(
            appViewState().globalMessageViewState,
            this._commonStrings.get("scheduleDeleteFailure"),
            MessageBarType.error,
            null,
            null,
            true,
            true,
            MESSAGE_BAR_AUTO_DISMISS_DELAY_MS,
            MESSAGE_BAR_AUTO_DISMISS_ANIMATION_TIME_MS
        );
    }

    /**
     * Render the left side of a list item for schedule teams associated with MS Teams
     */
    private renderScheduleTeamListItemLeft(teamInfo: ITeamInfoEntity, isFirstItem: boolean): JSX.Element {
        const handleViewButtonClicked = () => { this.handleViewTeamButtonClicked(teamInfo); };

        const isCurrentTeam = TeamStore().teamId === teamInfo.id;
        const itemClasses = classNames(styles.listItem, { [`${styles.selected}`]: isCurrentTeam });
        const showArchivedTeamIcon = teamInfo.isArchived && teamSetupPickerStore.selectedTeamCategoryType === TeamCategoryTypes.All;
        const ariaProps: AriaProperties = {
            role: AriaRoles.menuitem,
            selected: isCurrentTeam
        };
        return (
            <li
                { ...generateDomPropertiesForAria(ariaProps) }
                onClick={ handleViewButtonClicked }
                className={ itemClasses }
                tabIndex={ 0 }
                data-is-focusable={ true }
                ref={ isFirstItem ? this._firstItemRef : null }>
                {
                    // TODO robv - 2/22/2019 - Add team avatar support when available - for now, this is
                    // for testing layout when showIcons is set to true
                    showIcons && <Icon iconName={ "BorderDash" } className={ styles.linkIcon } />
                }
                <div className={ styles.name }>{ teamInfo.name }</div>
                {
                   showArchivedTeamIcon && <Archive16Regular />
                }
            </li>
        );
    }

    /**
     * Helper function to make "permanently deleted" bold in the checkbox label
     */
    private formatCheckboxLabelWithBoldText(text: string): JSX.Element {
        // Look for "permanently deleted" and make it bold
        const phrase = "permanently deleted";
        if (text.toLowerCase().includes(phrase.toLowerCase())) {
            const parts = text.split(new RegExp(`(${phrase})`, 'gi'));
            return (
                <>
                    {parts.map((part, index) =>
                        part.toLowerCase() === phrase.toLowerCase() ? (
                            <span key={index} className={styles.boldTeamName}>{part}</span>
                        ) : part
                    )}
                </>
            );
        }

        // If phrase not found, return the original text
        return <>{text}</>;
    }

    /**
     * Helper function to format dialog content with bold team name
     */
    private formatDialogContentWithBoldTeamName(): JSX.Element {
        const content = this._strings.get("deleteScheduleDialogContent");
        const parts = content.split("{0}");

        if (parts.length === 1) {
            // No placeholder found, just return the content
            return <>{content}</>;
        }

        return (
            <>
                {parts[0]}
                <span className={styles.boldTeamName}>{this.state.teamScheduleName}</span>
                {parts.slice(1).join("")}
            </>
        );
    }

    render() {
        return (
            <FocusZone>
                <div className={styles.content}>
                    {
                        this.renderScheduleTeamsPickerListContents()
                    }
                </div>
                <NewShiftsDialog
                    content={
                        <div className={styles.deleteScheduleDialogContent}>
                            <div>
                                {this.formatDialogContentWithBoldTeamName()}
                            </div>
                            <div>
                                <Checkbox
                                    checked={this.state.isDeleteScheduleDailogOpen && this.state.isDeleteScheduleConfirmed}
                                    onChange={(ev, data) => this.setState({ isDeleteScheduleConfirmed: data.checked })}
                                    label={this.formatCheckboxLabelWithBoldText(this._strings.get("deleteScheduleCheckboxLabel"))} />
                            </div>
                        </div>
                    }
                    title={this._strings.get("deleteScheduleDialogTitle").format(this.state.teamScheduleName)}
                    isOpen={this.state.isDeleteScheduleDailogOpen}
                    onDismiss={this.handleDeleteScheduleDismiss}
                    actions={<div className={styles.dialogActions}>
                        <Button onClick={this.handleDeleteScheduleDismiss}>{this._commonStrings.get("cancel")}</Button>
                        <Button onClick={this.handleDeleteSchedule} appearance="primary" disabled={!(this.state.isDeleteScheduleDailogOpen && this.state.isDeleteScheduleConfirmed)} data-testid="saveGroupButton">
                            {this._strings.get("deleteScheduleConfirmButton")}
                        </Button>
                    </div>}
                />
            </FocusZone>
        );
    }
}